import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Row } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import AccountMenu from 'components/account/AccountMenu';
import { LoginSection } from 'components/landing-pages-v2/shared/header/LoginSection';
import { useObservability } from 'helpers/observability';
import { usePrefetchNavTransactions } from 'helpers/useNavTransactions';

import { GlobalNavSVG } from './GlobalNavSVG';
import { useLargerThanSM } from './useLargerThanSM';

interface AccountSectionProps {
  isLoggedIn: boolean;
  isAuthenticating?: boolean;
  showLogin: boolean;
  initials: string[];
  redirectToLogin: () => void;
  onMobileMenuToggle: () => void;
  isMobileMenuVisible: boolean;
  hamburgerRef?: React.RefObject<HTMLButtonElement>;
  // When true, the menu/dots/sign-in render in the inverse (light) foreground for
  // use over a dark/transparent header; otherwise they use the primary foreground.
  inverse?: boolean;
}

export const AccountSection: React.FC<AccountSectionProps> = ({
  isLoggedIn,
  isAuthenticating = false,
  showLogin,
  initials,
  redirectToLogin,
  onMobileMenuToggle,
  isMobileMenuVisible,
  hamburgerRef,
  inverse,
}) => {
  const foregroundClass = inverse ? 'text-od-content-inverse-primary' : 'text-od-content-primary';
  const { trackJourneyEvent } = useObservability();
  const largerThanSM = useLargerThanSM();
  const [isExpanded, setExpanded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Warm the shared nav-transactions store from the always-mounted header so the
  // mobile menu (whose list only mounts on open) is populated instantly.
  usePrefetchNavTransactions();

  const toggleDropdown = useCallback(() => setExpanded((prev) => !prev), []);
  const closeDropdown = useCallback(() => setExpanded(false), []);

  useEffect(() => {
    if (!isExpanded) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        closeDropdown();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded, closeDropdown]);

  const handleEscape = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeDropdown();
      }
    },
    [closeDropdown],
  );

  useEffect(() => {
    if (!isExpanded) return;

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isExpanded, handleEscape]);

  const handleAvatarPress = useCallback(() => {
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'user-account-menu-click' });
    toggleDropdown();
  }, [trackJourneyEvent, toggleDropdown]);

  const handleLoginPress = useCallback(() => {
    trackJourneyEvent('platform', 'buttonClickEvent', { targetName: 'account-section-click' });
    redirectToLogin();
  }, [trackJourneyEvent, redirectToLogin]);

  const hamburger = (
    <Box className="min-[1024px]:hidden">
      <Box
        ref={hamburgerRef}
        onClick={onMobileMenuToggle}
        aria-label="Navigation menu"
        aria-haspopup="true"
        aria-expanded={isMobileMenuVisible}
        analyticsName="cosmos-landing-page-header-menu-button"
        className={cn(
          'flex cursor-pointer items-center justify-center focus:outline-none',
          foregroundClass,
        )}
      >
        <GlobalNavSVG isOpen={isMobileMenuVisible} />
      </Box>
    </Box>
  );

  return (
    <AccountSectionFrame containerRef={containerRef}>
      {isAuthenticating && (
        <>
          <Box
            aria-hidden
            className="skeleton hidden h-11 w-11 shrink-0 rounded-xl bg-od-bg-tertiary min-[1024px]:block"
          />
          {hamburger}
        </>
      )}
      {!isAuthenticating && isLoggedIn && (
        <>
          <Box
            onClick={handleAvatarPress}
            analyticsName="account-section-click"
            aria-label="User account menu"
            aria-haspopup="true"
            aria-expanded={isExpanded}
            className="group hidden cursor-pointer border-0 bg-transparent p-0 outline-none focus-visible:outline focus-visible:outline-1 focus-visible:outline-solid focus-visible:outline-offset-4 focus-visible:outline-[#81a2f2] min-[1024px]:block"
          >
            <Box className="inline-flex h-11 w-11 items-center justify-center overflow-hidden rounded-xl border border-transparent [background:linear-gradient(90deg,#eae8e0,#d7d4cd)_border-box_padding-box,linear-gradient(27.77deg,rgb(249_248_245/60%)_8.46%,transparent_26.47%)_border-box,linear-gradient(-172.67deg,rgb(249_248_245/70%)_17.64%,transparent_43.76%)_border-box,linear-gradient(#bdbab3,#bdbab3)_border-box] shadow-[0_0_0_1px_rgb(88_64_50_/_0.16),inset_0_0_0_1px_rgb(255_255_255_/_0.3),inset_0_-4px_0_0_rgb(88_64_50_/_0.1)] group-hover:shadow-[0_0_0_1px_rgb(88_64_50_/_0.3),inset_0_0_0_1px_rgb(255_255_255_/_0.3),inset_0_-4px_0_0_rgb(88_64_50_/_0.05)] group-active:shadow-[0_0_0_1px_rgb(88_64_50_/_0.3),inset_0_0_0_1px_rgb(255_255_255_/_0.3)]">
              <Text as="span" className="text-[14px] leading-none font-medium text-[#3c3733]">
                {initials}
              </Text>
            </Box>
          </Box>
          {hamburger}
        </>
      )}
      {!isAuthenticating && !isLoggedIn && (
        <Row className="items-center gap-1">
          <LoginSection showLogin={showLogin} onPress={handleLoginPress} inverse={inverse} />
          {hamburger}
        </Row>
      )}
      {largerThanSM && <AccountMenu isExpanded={isExpanded} isLoggedIn={isLoggedIn} />}
    </AccountSectionFrame>
  );
};

const AccountSectionFrame: React.FC<
  React.PropsWithChildren<{ containerRef: React.RefObject<HTMLDivElement> }>
> = ({ children, containerRef }) => (
  <Box as="div" ref={containerRef} className="relative flex h-11 w-max items-center">
    <Box className="flex items-center justify-end gap-2">{children}</Box>
  </Box>
);
